import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Column, Container, TextInput } from '@resurs/midas-react'
import Page from '../../../internal/Page'
import SettingsPanel from '../../../internal/SettingsPanel'
import { getText } from '../../../internal/TextLabels'
import './LogotypeSettings.css'
import { useToggle } from '../../../util/utility-hooks'

const availableImageTypes = ['image/jpg', 'image/jpeg', 'image/png']
const LogotypeSettings = ({ logotype, updateLogotype }) => {
  const inputRef = useRef()
  const formatErrorRef = useRef()
  const [edit, toggleEdit] = useToggle(false)
  const [selectedLogotype, setSelectedLogotype] = useState(null)
  const [selectedFileName, setSelectedFileName] = useState(null)
  const [fileSupportError, setFileSupportError] = useState(false)

  const getFileName = () => {
    if (selectedFileName) {
      return selectedFileName
    }
    if (logotype) {
      return logotype.fileName
    }

    return getText('logotypeSettings.missing')
  }

  const getLogotypeDataUri = () => {
    const imageUri = selectedLogotype || (logotype && logotype.logotype)
    return imageUri ? `data:image;base64,${imageUri}` : ''
  }

  const handleFileSelect = e => {
    e.preventDefault()
    inputRef.current.click()
  }

  const handleChange = selectedFile => {
    if (!selectedFile) {
      return
    }
    if (selectedFile && !availableImageTypes.includes(selectedFile.type)) {
      setFileSupportError(true)
      inputRef.current.value = ''
      return
    }

    const reader = new FileReader()
    reader.addEventListener(
      'load',
      () => {
        setSelectedLogotype(reader.result.split(',')[1])
        setSelectedFileName(selectedFile.name)
        setFileSupportError(false)
      },
      false,
    )
    reader.readAsDataURL(selectedFile)
  }

  const onCancelEdit = () => {
    setSelectedFileName(null)
    setSelectedLogotype(null)
    setFileSupportError(false)
    inputRef.current.value = ''
    toggleEdit()
  }

  const onSave = () => {
    if (selectedLogotype && !fileSupportError) {
      updateLogotype(selectedLogotype, selectedFileName)
    }
    setFileSupportError(false)
    toggleEdit()
  }

  const buttons = edit
    ? [
        {
          text: getText('default.cancel'),
          action: onCancelEdit,
        },
        {
          text: getText('default.save'),
          disabled: !selectedLogotype || fileSupportError,
          type: 'primary',
          action: onSave,
        },
      ]
    : [
        {
          text: getText('default.edit'),
          action: toggleEdit,
        },
      ]

  return (
    <>
      <Container>
        <div
          ref={formatErrorRef}
          style={{
            color: 'red',
            display: `${fileSupportError ? 'block' : 'none'}`,
          }}
        >
          {getText('logotypeSettings.imageTypeError')}
        </div>
      </Container>
      <Page>
        <SettingsPanel
          header={getText('logotypeSettings.header')}
          buttons={buttons}
        >
          <Column col4>
            <img
              style={{ height: 'auto', width: '100%' }}
              alt="Logotype"
              src={getLogotypeDataUri()}
            />
          </Column>
          <Column col8>
            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={e => {
                  handleChange(e.target.files[0])
                }}
              />
              <TextInput
                id="image-name"
                disabled
                dataCy="image-name"
                skipValidation
                value={getFileName()}
              >
                <div style={{ marginLeft: '1rem' }} />

                <Button
                  disabled={!edit}
                  onClick={handleFileSelect}
                  text={getText('logotypeSettings.select')}
                />
              </TextInput>
            </div>
          </Column>
        </SettingsPanel>
      </Page>
    </>
  )
}

LogotypeSettings.propTypes = {
  updateLogotype: PropTypes.func.isRequired,
  logotype: PropTypes.object,
}

LogotypeSettings.defaultProps = {
  logotype: null,
}

export default LogotypeSettings
